import { useEffect, useMemo } from 'react'
import { initialQueryState } from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import { CustomSelect, IOption, TableFilter, TableFilters } from 'app/components'
import { isVinculoMedicoAtivo, useAuth } from 'app/modules/auth'
import { inscricaoDoVinculo } from '../../core/_models'
import { useListFilters } from '../core/ListFiltersProvider'
import { useQueryRequest } from '../core/QueryRequestProvider'

/**
 * Seletor da empresa de vínculo. Ela só é perguntada quando faz diferença: o estoque é da pessoa e
 * a inscrição (CRM/UF) vem do vínculo, então com a mesma inscrição em todas as empresas os dados
 * são idênticos e a escolha seria ruído — aí a primeira é escolhida sozinha e o seletor não aparece.
 * Sem "Limpar filtros": sem empresa a consulta nem roda e os cards somem.
 */
export const SncrBlocosSearchComponent = () => {
  const { t } = useT()
  const { currentUser } = useAuth()
  const { updateState } = useQueryRequest()
  const { filters, setFilter } = useListFilters()

  // Empresas de vínculo médico e se a inscrição é a mesma em todas — tudo do payload do login,
  // sem chamada: `crm` já vem como "número/UF" e `conselho` traz a sigla.
  const { opcoes, mesmaInscricao } = useMemo(() => {
    const vinculos = (currentUser?.vinculos || []).filter(
      (v) => isVinculoMedicoAtivo(v) && v.empresa?.id != null
    )
    const inscricoes = new Set(vinculos.map((v) => inscricaoDoVinculo(v) ?? ''))
    const mesmaInscricao = inscricoes.size === 1
    const vistos = new Set<number>()
    const opcoes: IOption<number>[] = vinculos
      .filter((v) => {
        const id = Number(v.empresa?.id)
        if (vistos.has(id)) return false
        vistos.add(id)
        return true
      })
      .map((v) => {
        const empresa =
          v.empresa?.nomeFantasia ||
          v.empresa?.nome ||
          t('sncr.lista.empresa_sem_nome', { id: v.empresa?.id })
        // A escolha só existe porque a inscrição muda de empresa para empresa: ela vai no rótulo
        // para o médico ver por qual CRM/UF a numeração sai, sem precisar lembrar de cada vínculo.
        const inscricao = mesmaInscricao ? undefined : inscricaoDoVinculo(v)
        return {
          label: inscricao
            ? t('sncr.lista.empresa_com_inscricao', { empresa, inscricao })
            : empresa,
          value: Number(v.empresa?.id),
        }
      })
    return { opcoes, mesmaInscricao }
  }, [currentUser, t])

  const escolhaAutomatica = opcoes.length === 1 || mesmaInscricao
  const mostrarSelecao = opcoes.length > 1 && !mesmaInscricao
  const empresaId = filters.empresa?.value

  useEffect(() => {
    if (!filters.empresa && escolhaAutomatica) setFilter('empresa', opcoes[0])
  }, [filters.empresa, escolhaAutomatica, opcoes, setFilter])

  // A empresa vai em `params` (query param `idEmpresa` da rota), não em `search`.
  useEffect(() => {
    updateState({ ...initialQueryState, params: { empresaId } })
  }, [empresaId, updateState])

  if (!mostrarSelecao) return null

  return (
    <TableFilters>
      <TableFilter
        label={t('sncr.lista.empresa')}
        hint={t('sncr.lista.empresa_info')}
        htmlFor='sncr-empresa'
        wide
      >
        <CustomSelect
          inputId='sncr-empresa'
          options={opcoes}
          value={filters.empresa}
          placeholder={t('sncr.lista.empresa_placeholder')}
          onChange={(opcao?: IOption<number> | null) => setFilter('empresa', opcao ?? undefined)}
        />
      </TableFilter>
    </TableFilters>
  )
}
